import React, { useState, useEffect, useCallback } from 'react';
import { payoutAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  DollarSign, FileText, AlertTriangle, CheckCircle2, Clock,
  Download, Loader2, ArrowUpDown, TrendingUp, ArrowRight, Trash2,
} from 'lucide-react';
import api from '@/lib/api';

const fmtMoney = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '$0.00';
  return n < 0 ? `-$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
};

const SEVERITY_STYLES = {
  critical: 'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  info: 'bg-sky-50 border-sky-200 text-sky-700',
};

const SEVERITY_ICONS = {
  critical: <AlertTriangle size={16} className="text-red-500" />,
  warning: <AlertTriangle size={16} className="text-amber-500" />,
  info: <Clock size={16} className="text-sky-500" />,
};

export default function ReconciliationPage() {
  const { user } = useAuth();
  const canView = isKRRole(user);
  const isSuperAdmin = user?.active_context?.role === 'KR_SuperAdmin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ statement_month: 'all', payee_type: 'all' });
  const [exporting, setExporting] = useState(false);
  const [clearDialog, setClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);

  const handleClearLegacyData = async () => {
    setClearing(true);
    try {
      const res = await api.delete('/payout/legacy-data');
      toast.success(`Legacy data cleared: ${res.data.deleted.payout_ledger} ledger entries, ${res.data.deleted.payout_statements} statements, ${res.data.deleted.payout_payments} payments`);
      setClearDialog(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear legacy data');
    }
    setClearing(false);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.statement_month !== 'all') params.statement_month = filters.statement_month;
      if (filters.payee_type !== 'all') params.payee_type = filters.payee_type;
      const res = await payoutAPI.reconciliation.overview(params);
      setData(res.data);
    } catch {
      toast.error('Failed to load reconciliation data');
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await payoutAPI.reconciliation.exportCsv(filters.statement_month !== 'all' ? filters.statement_month : undefined);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `reconciliation_${filters.statement_month || 'all'}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    }
    setExporting(false);
  };

  if (!canView) {
    return <div className="p-8 text-center text-slate-500">You do not have permission to view this page.</div>;
  }

  const o = data?.overview || {};
  const errors = data?.errors || [];
  const stmts = data?.statements || {};
  const filterOpts = data?.filter_options || {};

  const matchRate = o.ledger_count > 0 ? Math.round(((o.ledger_count - o.unassigned_count) / o.ledger_count) * 100) : 0;
  const paymentRate = o.statement_total > 0 ? Math.round((o.total_paid / o.statement_total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="reconciliation-page">
      {/* Historical Data Banner */}
      <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3" data-testid="legacy-warning-banner">
        <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
        <div className="flex-1">
          <p className="font-semibold text-amber-800">HISTORICAL DATA – NO LONGER ACTIVE</p>
          <p className="text-sm text-amber-700 mt-1">
            This page displays legacy payout data. All new override reconciliation work should be done in the{' '}
            <a href="/accounting/override" className="underline font-medium hover:text-amber-900">Override Reconciliation</a> module.
          </p>
        </div>
        {isSuperAdmin && (
          <Button 
            variant="destructive" 
            size="sm" 
            className="gap-1.5 flex-shrink-0" 
            onClick={() => setClearDialog(true)}
            data-testid="clear-legacy-data-btn"
          >
            <Trash2 size={14} /> Clear Legacy Data
          </Button>
        )}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Accounting (Legacy)</p>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Legacy Reconciliation Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Historical view of legacy ledger entries, statements, and payments</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filters.statement_month} onValueChange={v => setFilters(f => ({ ...f, statement_month: v }))}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="recon-month-filter"><SelectValue placeholder="All Months" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {(filterOpts.months || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.payee_type} onValueChange={v => setFilters(f => ({ ...f, payee_type: v }))}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="recon-payee-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Agent">Agent</SelectItem>
              <SelectItem value="Agency">Agency</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="gap-1.5" data-testid="recon-export-btn">
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-slate-400" size={32} />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="recon-summary-cards">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                    <DollarSign size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-900">{fmtMoney(o.ledger_total)}</p>
                    <p className="text-xs text-slate-500">Ledger Total ({o.ledger_count} entries)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-sky-600 flex items-center justify-center">
                    <FileText size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-900">{fmtMoney(o.statement_total)}</p>
                    <p className="text-xs text-slate-500">Statement Total ({o.statement_count} stmts)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
                    <CheckCircle2 size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-900">{fmtMoney(o.total_paid)}</p>
                    <p className="text-xs text-slate-500">Total Paid</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                    <Clock size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-900">{fmtMoney(o.outstanding_balance)}</p>
                    <p className="text-xs text-slate-500">Outstanding Balance</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Match Rate & Payment Progress */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                  <ArrowUpDown size={14} /> Ledger Assignment Rate
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="flex items-end gap-3">
                  <span className="text-3xl font-bold text-slate-900">{matchRate}%</span>
                  <span className="text-sm text-slate-500 pb-1">assigned to statements</span>
                </div>
                <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-sky-600 rounded-full transition-all" style={{ width: `${matchRate}%` }} />
                </div>
                <div className="mt-2 flex justify-between text-xs text-slate-400">
                  <span>{o.ledger_count - o.unassigned_count} assigned</span>
                  <span>{o.unassigned_count} unassigned ({fmtMoney(o.unassigned_total)})</span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                  <TrendingUp size={14} /> Payment Progress
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="flex items-end gap-3">
                  <span className="text-3xl font-bold text-slate-900">{paymentRate}%</span>
                  <span className="text-sm text-slate-500 pb-1">of statement totals paid</span>
                </div>
                <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-600 rounded-full transition-all" style={{ width: `${paymentRate}%` }} />
                </div>
                <div className="mt-2 flex justify-between text-xs text-slate-400">
                  <span>{fmtMoney(o.total_paid)} paid</span>
                  <span>{fmtMoney(o.pending_amount)} remaining</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alerts/Errors */}
          {errors.length > 0 && (
            <div className="space-y-2" data-testid="recon-alerts">
              <h3 className="text-sm font-medium text-slate-700">Discrepancies & Alerts</h3>
              {errors.map((e, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${SEVERITY_STYLES[e.severity] || SEVERITY_STYLES.info}`}>
                  {SEVERITY_ICONS[e.severity] || SEVERITY_ICONS.info}
                  <span className="text-sm flex-1">{e.message}</span>
                  <Badge variant="outline" className="text-xs">{e.count}</Badge>
                </div>
              ))}
            </div>
          )}

          {/* Statement Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="recon-statement-breakdown">
            <StmtColumn title="Unpaid Statements" items={stmts.unpaid || []} color="amber" icon={<Clock size={14} />} />
            <StmtColumn title="Partially Paid" items={stmts.partial || []} color="sky" icon={<ArrowRight size={14} />} />
            <StmtColumn title="Fully Paid" items={stmts.paid || []} color="emerald" icon={<CheckCircle2 size={14} />} />
          </div>

          {/* Unassigned Entries */}
          {(data?.unassigned_entries || []).length > 0 && (
            <Card data-testid="recon-unassigned-section">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-700">
                  <AlertTriangle size={14} /> Unassigned Ledger Entries ({data.unassigned_entries.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                        <th className="pb-2 pr-4">Payee</th>
                        <th className="pb-2 pr-4">Type</th>
                        <th className="pb-2 pr-4">Month</th>
                        <th className="pb-2 pr-4 text-right">Amount</th>
                        <th className="pb-2">Entry Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.unassigned_entries.slice(0, 20).map((e, i) => (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 pr-4 font-medium text-slate-800">{e.payee_name}</td>
                          <td className="py-2 pr-4 text-slate-500">{e.payee_type}</td>
                          <td className="py-2 pr-4 font-mono text-xs text-slate-500">{e.statement_month}</td>
                          <td className="py-2 pr-4 text-right font-mono">{fmtMoney(e.amount)}</td>
                          <td className="py-2"><Badge variant="outline" className="text-xs">{e.entry_type}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {data.unassigned_entries.length > 20 && (
                    <p className="text-xs text-slate-400 mt-2">Showing first 20 of {data.unassigned_entries.length} entries</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Clear Legacy Data Confirmation Dialog */}
      <Dialog open={clearDialog} onOpenChange={setClearDialog}>
        <DialogContent className="max-w-md" data-testid="clear-legacy-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600 flex items-center gap-2">
              <AlertTriangle size={20} /> Clear All Legacy Data
            </DialogTitle>
            <DialogDescription>
              This will permanently delete all data from the legacy payout system:
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2 text-sm">
            <p><strong>• payout_ledger</strong> — All ledger entries</p>
            <p><strong>• payout_statements</strong> — All legacy statements</p>
            <p><strong>• payout_payments</strong> — All legacy payments</p>
          </div>
          <p className="text-sm text-slate-600">
            This action <strong>cannot be undone</strong>. The Override Reconciliation data will NOT be affected.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleClearLegacyData} disabled={clearing} data-testid="confirm-clear-btn">
              {clearing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
              Yes, Clear All Legacy Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StmtColumn({ title, items, color, icon }) {
  const colorMap = {
    amber: 'bg-amber-50 border-amber-200',
    sky: 'bg-sky-50 border-sky-200',
    emerald: 'bg-emerald-50 border-emerald-200',
  };
  const textMap = { amber: 'text-amber-700', sky: 'text-sky-700', emerald: 'text-emerald-700' };

  return (
    <Card className={`${colorMap[color]} border`}>
      <CardHeader className="pb-2">
        <CardTitle className={`text-sm font-medium flex items-center gap-2 ${textMap[color]}`}>
          {icon} {title} ({items.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 max-h-64 overflow-y-auto">
        {items.length === 0 ? (
          <p className="text-xs text-slate-400 py-4 text-center">None</p>
        ) : (
          items.map((s, i) => (
            <div key={i} className="bg-white rounded-md p-2.5 border border-slate-200 shadow-sm">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium text-slate-800">{s.payee_name}</p>
                  <p className="text-xs text-slate-400">{s.statement_month} | {s.payee_type}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold font-mono text-slate-900">{fmtMoney(s.total)}</p>
                  {s.amount_paid > 0 && <p className="text-[10px] text-emerald-600">Paid: {fmtMoney(s.amount_paid)}</p>}
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
